import { BulkEditPanelData } from 'features/drilldown/panels/global/bulkEdit/BulkEditPanel'
import { EntryWithEdits } from 'features/drilldown/panels/global/bulkEdit/types'
import { parseTypedValue } from 'features/drilldown/panels/global/bulkEdit/utils'
import { OntologyFieldType } from 'features/dynamicOntology'
import { useRolodexEdit } from 'features/fde'
import { useGeckoMessages } from 'features/notifications'
import { useCallback } from 'react'
import { RolodexEntryPatchJson, RolodexEntryType } from 'types'

interface UseBulkEditActionsProps {
  panelData: BulkEditPanelData
  entriesWithEdits: ReadonlyArray<EntryWithEdits>
  totalCount: number
  propertyKey: string | null
  selectedFieldType: OntologyFieldType
  forceRefresh: () => Promise<void>
  resetState: () => void
  setIsSubmitting: (isSubmitting: boolean) => void
  setIsModalVisible: (isVisible: boolean) => void
}

export const useBulkEditActions = ({
  panelData,
  entriesWithEdits,
  propertyKey,
  selectedFieldType,
  forceRefresh,
  resetState,
  setIsSubmitting,
  setIsModalVisible,
}: UseBulkEditActionsProps) => {
  const { rolodexPatch } = useRolodexEdit()
  const { successMessage, warningMessage, errorMessage } = useGeckoMessages()

  const handleBulkEditSubmit = useCallback(() => {
    setIsModalVisible(true)
  }, [setIsModalVisible])

  const handleModalConfirm = useCallback(async () => {
    setIsSubmitting(true)
    try {
      if (!propertyKey) {
        errorMessage('Missing property key for bulk edit - unable to proceed.')
        return
      }

      const entriesWithChanges = entriesWithEdits.filter(
        (row) => row.hasDifference
      )

      if (entriesWithChanges.length === 0) {
        warningMessage(
          'No changes detected. All values are already set to the desired values.'
        )
        return
      }

      const patchData: ReadonlyArray<RolodexEntryPatchJson> =
        entriesWithChanges.map((row) => {
          const data: Record<string, unknown> = {}

          if (panelData.isEditingCustomProperty) {
            data[panelData.customPropertyName] = parseTypedValue(
              row.newValue as string,
              panelData.customPropertyType,
              warningMessage
            )
          } else {
            const [, fieldName] = panelData.selectedField.split('.')
            if (fieldName) {
              data[fieldName] = parseTypedValue(
                row.newValue as string,
                selectedFieldType,
                warningMessage
              )
            }
          }

          if (
            !RolodexEntryType[
              panelData.selectedType as keyof typeof RolodexEntryType
            ]
          ) {
            console.warn(
              `Type ${panelData.selectedType} should be registered in RolodexEntryType`
            )
          }

          return {
            id: row.id,
            type: RolodexEntryType[
              panelData.selectedType as keyof typeof RolodexEntryType
            ],
            data,
          }
        })

      await rolodexPatch([...patchData])
      successMessage(
        `Successfully updated ${entriesWithChanges.length} entries`
      )
      resetState()
      await forceRefresh()
    } catch (error) {
      console.error('Error updating entries:', error)
      errorMessage('Failed to update entries. Please try again.')
    } finally {
      setIsSubmitting(false)
      setIsModalVisible(false)
    }
  }, [
    setIsSubmitting,
    propertyKey,
    entriesWithEdits,
    rolodexPatch,
    successMessage,
    resetState,
    forceRefresh,
    errorMessage,
    panelData.selectedType,
    panelData.isEditingCustomProperty,
    panelData.customPropertyName,
    panelData.customPropertyType,
    panelData.selectedField,
    warningMessage,
    setIsModalVisible,
    selectedFieldType,
  ])

  const handleModalCancel = useCallback(() => {
    setIsModalVisible(false)
  }, [setIsModalVisible])

  return {
    handleBulkEditSubmit,
    handleModalConfirm,
    handleModalCancel,
  }
}
